import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, AlertTriangle, FileDown, FileText, Plus, Check, X, ExternalLink, RefreshCw } from 'lucide-react';
import type { CaseDetail, CaseStatus, CaseSummary, StepKind } from '@cortex/shared';
import { Api, getToken } from '../../lib/api';
import { useUi } from '../../store/ui';
import { useSessionContext } from '../../hooks/sessionContext';
import { qk } from '../../hooks/useVault';

const STATUS_CLASS: Record<CaseStatus, string> = { OPEN: 'crit', DIAGNOSING: 'warn', IDENTIFIED: 'teal', RESOLVED: 'ok', CLOSED: '', ABANDONED: '' };
const KIND_CLASS: Record<StepKind, string> = { OBSERVATION: '', HYPOTHESIS: 'warn', TEST: 'teal', RESULT: 'ok', ACTION: 'accent', NOTE: '' };

export function useOpenCases() {
  return useQuery({ queryKey: ['cases', 'open-count'], queryFn: () => Api.cases.list({ open: true, limit: 1 }), refetchInterval: 30_000, staleTime: 10_000 });
}

export function CasesView() {
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const [newCase, setNewCase] = useState(false);
  const session = useSessionContext();
  const list = useQuery({ queryKey: ['cases', filter, q], queryFn: () => Api.cases.list({ open: filter === 'open' || undefined, q: q || undefined, limit: 200 }), staleTime: 10_000, refetchInterval: 20_000 });
  const rows = useMemo(() => list.data?.cases ?? [], [list.data]);
  const current = sel ?? rows[0]?.id ?? null;
  return (
    <div className="cases-view">
      <div className="cases-list">
        <div className="cases-head">
          <div className="modes">
            <button className={filter === 'open' ? 'on' : ''} onClick={() => setFilter('open')}>open</button>
            <button className={filter === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>all</button>
          </div>
          <input type="text" placeholder="search ref, symptom…" value={q} onChange={(e) => setQ(e.target.value)} />
          {session.user?.role !== 'VIEWER' && <button className="icon" title="Open a case by hand" onClick={() => setNewCase(true)}><Plus size={14} /></button>}
        </div>
        {rows.length === 0 && <div className="dock-empty">{filter === 'open' ? 'No open cases.' : 'No cases yet.'} Faults worked in the agent\'s Faultfind mode open cases automatically.</div>}
        {rows.map((c) => (
          <button key={c.id} className={`case-row${c.id === current ? ' on' : ''}`} onClick={() => setSel(c.id)}>
            <span className="top">
              <span className="mono ref">{c.ref}</span>
              <span className={`chip ${STATUS_CLASS[c.status]}`} style={{ fontSize: 9 }}>{c.status.toLowerCase()}</span>
              <span className={`sev s${c.severity}`} title={`severity ${c.severity}`}>S{c.severity}</span>
            </span>
            <span className="t">{c.title}</span>
            <span className="faint mono" style={{ fontSize: 10 }}>{strip(c.site) || '—'} · {new Date(c.openedAt).toLocaleDateString()} · {c.stepCount} steps</span>
          </button>
        ))}
      </div>
      <div className="cases-detail">{current ? <CaseDetailPane id={current} /> : <div className="pane-empty">Select a case.</div>}</div>
      {newCase && <NewCaseDialog onClose={(id) => (setNewCase(false), id && setSel(id))} />}
    </div>
  );
}

function CaseDetailPane({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['case', id], queryFn: () => Api.cases.get(id), staleTime: 5_000, refetchInterval: 10_000 });
  const openNote = useUi((s) => s.openNote);
  const toast = useUi((s) => s.toast);
  const session = useSessionContext();
  const [closing, setClosing] = useState(false);
  const [stepText, setStepText] = useState('');
  const [stepKind, setStepKind] = useState<StepKind>('OBSERVATION');
  const c = q.data;
  if (!c) return <div className="pane-empty">Loading…</div>;
  const isOpen = !['CLOSED', 'ABANDONED', 'RESOLVED'].includes(c.status) || c.status === 'RESOLVED';
  const canEdit = session.user?.role !== 'VIEWER' && !['CLOSED', 'ABANDONED'].includes(c.status);
  const refresh = () => (qc.invalidateQueries({ queryKey: ['case', id] }), qc.invalidateQueries({ queryKey: ['cases'] }));
  const addStep = async () => {
    if (!stepText.trim()) return;
    await Api.cases.addStep(c.id, { kind: stepKind, summary: stepText.trim() });
    setStepText('');
    refresh();
  };
  const ttr = c.closedAt ? human(Date.parse(c.closedAt) - Date.parse(c.openedAt)) : human(Date.now() - Date.parse(c.openedAt)) + ' so far';
  return (
    <div className="case-detail">
      <div className="case-title">
        <span className="mono ref">{c.ref}</span>
        <span className={`chip ${STATUS_CLASS[c.status]}`}>{c.status.toLowerCase()}</span>
        <span className={`sev s${c.severity}`}>S{c.severity}</span>
        <span className="spacer" />
        {c.noteId && <button className="ghost" onClick={() => openNote(c.noteId!)}><FileText size={12} /> Note</button>}
        <a className="ghost btn-link" href={`/api/cases/${c.id}/export.pdf?inline=true&access_token=`} onClick={(e) => (e.preventDefault(), openExport(c.id, 'pdf'))} title="Fault report PDF"><FileDown size={12} /> PDF</a>
        <button className="ghost" onClick={() => openExport(c.id, 'md')} title="Markdown"><ExternalLink size={12} /> .md</button>
        {canEdit && <button className="primary" onClick={() => setClosing(true)}><Check size={12} /> Close case</button>}
      </div>
      <h2>{c.title}</h2>
      {c.noteDiverged && (
        <div className="case-diverged">
          <AlertTriangle size={13} />
          <span>
            This case note has been edited by hand, so the agent has stopped rendering over it. Your edit is safe. New steps are recorded here but
            are not being written to the note.
          </span>
          {canEdit && (
            <button
              className="ghost"
              title="Replace the note with the agent's rendering. The hand-edited version stays in the vault's git history."
              onClick={() =>
                void Api.cases.resync(c.id)
                  .then(() => (toast('Case note rebuilt from the case record'), refresh()))
                  .catch((e) => toast((e as Error).message, 'crit'))
              }
            >
              <RefreshCw size={12} /> Rebuild note
            </button>
          )}
        </div>
      )}
      <div className="case-meta">
        <span>site <b>{strip(c.site) || '—'}</b></span>
        <span>devices <b>{c.devices.map(strip).join(', ') || '—'}</b></span>
        <span>opened <b>{new Date(c.openedAt).toLocaleString()}</b></span>
        <span>{c.closedAt ? 'time to resolve' : 'elapsed'} <b>{ttr}</b></span>
        {c.runbookKey && <span>runbook <b className="mono">{c.runbookKey}</b></span>}
        {c.sessionId && <span>session <b className="mono">{c.sessionId}</b></span>}
      </div>
      <div className="case-block"><div className="label">Symptom</div><p>{c.symptom}</p></div>
      {c.rootCause && <div className="case-block"><div className="label">Root cause</div><p>{c.rootCause}</p></div>}
      {c.resolution && <div className="case-block"><div className="label">Resolution</div><p>{c.resolution}</p></div>}
      {c.followUp.length > 0 && <div className="case-block"><div className="label">Follow-up</div><ul>{c.followUp.map((f, i) => <li key={i}>{f}</li>)}</ul></div>}
      <div className="label" style={{ marginTop: 14 }}>Timeline · {c.steps.length} steps</div>
      <ol className="timeline">
        {c.steps.map((s) => (
          <li key={s.id} className={`step ${s.kind.toLowerCase()}`}>
            <span className="when mono">{new Date(s.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            <span className={`chip ${KIND_CLASS[s.kind]}`} style={{ fontSize: 9, minWidth: 82, justifyContent: 'center' }}>{s.kind}</span>
            <span className="what">
              {s.summary}
              {s.detail && typeof s.detail.tool === 'string' && (
                <span className="faint mono" style={{ fontSize: 10, display: 'block' }}>
                  {String(s.detail.tool)}{typeof s.detail.durationMs === 'number' ? ` · ${s.detail.durationMs} ms` : ''}{s.detail.credential ? ` · ${String(s.detail.credential)}` : ''}{s.toolCallId ? ` · call ${s.toolCallId}` : ''}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
      {canEdit && (
        <div className="step-add">
          <select value={stepKind} onChange={(e) => setStepKind(e.target.value as StepKind)}>{(['OBSERVATION', 'HYPOTHESIS', 'TEST', 'RESULT', 'ACTION', 'NOTE'] as StepKind[]).map((k) => <option key={k}>{k}</option>)}</select>
          <input type="text" placeholder="Add a step by hand (Enter)…" value={stepText} onChange={(e) => setStepText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void addStep()} />
          <button className="ghost" onClick={() => void addStep()}>Add</button>
        </div>
      )}
      {closing && (
        <CloseDialog
          onClose={() => setClosing(false)}
          onSubmit={async (p) => {
            try {
              await Api.cases.close(c.id, p);
              toast(`${c.ref} ${p.status.toLowerCase()}`);
              setClosing(false);
              refresh();
              qc.invalidateQueries({ queryKey: qk.tree });
            } catch (err) {
              toast((err as Error).message, 'crit');
            }
          }}
        />
      )}
      {isOpen && null}
    </div>
  );
}

function CloseDialog({ onClose, onSubmit }: { onClose: () => void; onSubmit: (p: { rootCause: string; resolution: string; followUp: string[]; status: 'RESOLVED' | 'CLOSED' | 'ABANDONED' }) => Promise<void> }) {
  const [rootCause, setRootCause] = useState('');
  const [resolution, setResolution] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [status, setStatus] = useState<'RESOLVED' | 'CLOSED' | 'ABANDONED'>('RESOLVED');
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" style={{ width: 520 }}>
        <div className="dialog-head">Close case<span className="spacer" /><button className="icon" onClick={onClose}><X size={14} /></button></div>
        <div className="dialog-body">
          <label>Root cause (an honest "needs hands on site: injector or cable" is fine)<textarea rows={3} value={rootCause} onChange={(e) => setRootCause(e.target.value)} /></label>
          <label>Resolution<textarea rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)} /></label>
          <label>Follow-up actions (one per line)<textarea rows={3} value={followUp} onChange={(e) => setFollowUp(e.target.value)} /></label>
          <label>Outcome<select value={status} onChange={(e) => setStatus(e.target.value as 'RESOLVED')}><option value="RESOLVED">Resolved (fixed)</option><option value="CLOSED">Closed (resolved and reported)</option><option value="ABANDONED">Abandoned / escalated (could not be pursued)</option></select></label>
        </div>
        <div className="dialog-foot">
          <button className="ghost" onClick={onClose}>Cancel</button>
          <button className="primary" disabled={rootCause.trim().length < 3 || resolution.trim().length < 3} onClick={() => void onSubmit({ rootCause: rootCause.trim(), resolution: resolution.trim(), followUp: followUp.split(/\r?\n/).map((s) => s.trim()).filter(Boolean), status })}><Check size={12} /> Close</button>
        </div>
      </div>
    </div>
  );
}

function NewCaseDialog({ onClose }: { onClose: (id?: string) => void }) {
  const toast = useUi((s) => s.toast);
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [symptom, setSymptom] = useState('');
  const [severity, setSeverity] = useState(3);
  const [site, setSite] = useState('');
  const [devices, setDevices] = useState('');
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" style={{ width: 520 }}>
        <div className="dialog-head"><Activity size={14} /> Open a case<span className="spacer" /><button className="icon" onClick={() => onClose()}><X size={14} /></button></div>
        <div className="dialog-body">
          <label>Title<input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="SWC backhaul down" /></label>
          <label>Symptom as reported<textarea rows={3} value={symptom} onChange={(e) => setSymptom(e.target.value)} /></label>
          <div className="grid2">
            <label>Severity<select value={severity} onChange={(e) => setSeverity(Number(e.target.value))}><option value={1}>1 site down</option><option value={2}>2 service degraded</option><option value={3}>3 single device</option><option value={4}>4 cosmetic</option></select></label>
            <label>Site (note title)<input type="text" value={site} onChange={(e) => setSite(e.target.value)} /></label>
          </div>
          <label>Devices (comma-separated note titles)<input type="text" value={devices} onChange={(e) => setDevices(e.target.value)} /></label>
        </div>
        <div className="dialog-foot">
          <button className="ghost" onClick={() => onClose()}>Cancel</button>
          <button className="primary" disabled={title.trim().length < 3 || symptom.trim().length < 3} onClick={() => void Api.cases.create({ title: title.trim(), symptom: symptom.trim(), severity, site: site.trim() || undefined, devices: devices.split(',').map((s) => s.trim()).filter(Boolean) }).then((c) => (toast(`Opened ${c.ref}`), qc.invalidateQueries({ queryKey: ['cases'] }), qc.invalidateQueries({ queryKey: qk.tree }), onClose(c.id))).catch((e) => toast((e as Error).message, 'crit'))}><Check size={12} /> Open</button>
        </div>
      </div>
    </div>
  );
}

function openExport(id: string, kind: 'pdf' | 'md'): void {
  // Auth is a bearer header; fetch to a blob so the token never lands in a URL.
  void fetch(`/api/cases/${encodeURIComponent(id)}/export.${kind}${kind === 'pdf' ? '?inline=true' : ''}`, { headers: { authorization: `Bearer ${getToken() ?? ''}` } })
    .then(async (r) => {
      if (!r.ok) throw new Error(`${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    })
    .catch((e) => useUi.getState().toast(`Export failed: ${(e as Error).message}`, 'crit'));
}

function strip(s: string | null): string {
  return (s ?? '').replace(/^\[\[|\]\]$/g, '').split('|')[0];
}
function human(ms: number): string {
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ${m % 60} min`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

export type { CaseDetail, CaseSummary };
